import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { PayoutMethodType, PayoutInput, RecipientInput } from "../interfaces/payout";
import type { FundingMethod } from "../utils/catalog";
import type { IQuoteResponse } from "../services/api";

/**
 * The composed send, captured on the TransferPage confirm. Persisted so it
 * survives the CIP redirect: on return from `/cip`, the SendingMoneyPage reads
 * the quote + draft and creates the operation.
 */
export interface ISendDraft {
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  /** Amount the sender pays, in the source currency (send-anchored). */
  amount: number;
  fundingMethod: FundingMethod;
  payoutMethod: PayoutMethodType;
  payout: PayoutInput;
  recipient: RecipientInput;
  /** SKU of the pre-quote option chosen. */
  capabilityKey?: string;
  /** Stored card id pinned for CARD funding (undefined for cash/other). */
  fundingPaymentMethodId?: string;
}

interface SendState {
  draft: ISendDraft | null;
  setDraft: (draft: ISendDraft | null) => void;

  /** Binding quote persisted so the signedQuote survives the CIP redirect. */
  quoteState: IQuoteResponse | null;
  setQuoteState: (quoteState: IQuoteResponse | null) => void;

  reset: () => void;
}

export const useSendStore = create<SendState>()(
  persist(
    (set) => ({
      draft: null,
      setDraft: (draft) => set(() => ({ draft })),
      quoteState: null,
      setQuoteState: (quoteState) => set(() => ({ quoteState })),
      reset: () => set(() => ({ draft: null, quoteState: null })),
    }),
    {
      name: "raas-widgets-flow-send-draft",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
